import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/widgets";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, Download, FileWarning } from "lucide-react";
import dayjs from "dayjs";
import { Checkbox } from "@/components/ui/checkbox";
import { GoogleRatingsTab } from "@/components/settings/GoogleRatingsTab";
import { OemScorecardTab } from "@/components/settings/OemScorecardTab";

const ROLE_OPTS = [
  { value: "admin", label: "AGM Customer Care (Admin)" },
  { value: "md", label: "Managing Director" },
  { value: "vp_sales", label: "VP Sales" },
  { value: "vp_service", label: "VP Service" },
  { value: "cluster_head_sales", label: "Cluster Head – Sales" },
  { value: "cluster_head_service", label: "Cluster Head – Service" },
  { value: "outlet_sales_manager", label: "Outlet Sales Manager" },
  { value: "outlet_service_manager", label: "Outlet Service Manager" },
  { value: "crm_executive", label: "CCM / CRM Executive" },
];

// Suggested function scope prefills when role changes (admin can override)
const SUGGESTED_FUNCTION = {
  admin: "Both", md: "Both",
  vp_sales: "Sales", vp_service: "Service",
  cluster_head_sales: "Sales", cluster_head_service: "Service",
  outlet_sales_manager: "Sales", outlet_service_manager: "Service",
  crm_executive: "Both",
};

const SETTINGS_TABS = ["users", "outlets", "clusters", "categories",
  "function-mapping", "escalation", "distribution", "google-ratings", "oem-scorecard", "data"];

export default function SettingsPage() {
  const [params] = useSearchParams();
  const requestedTab = params.get("tab");
  const initialTab = SETTINGS_TABS.includes(requestedTab) ? requestedTab : "users";
  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Users, locations, clusters, categories, function mapping, escalation thresholds and report distribution" />
      <Tabs defaultValue={initialTab} data-testid="settings-tabs">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="settings-users-tab">Users</TabsTrigger>
          <TabsTrigger value="outlets" data-testid="settings-outlets-tab">Locations</TabsTrigger>
          <TabsTrigger value="clusters" data-testid="settings-clusters-tab">Clusters</TabsTrigger>
          <TabsTrigger value="categories" data-testid="settings-categories-tab">Categories</TabsTrigger>
          <TabsTrigger value="function-mapping" data-testid="settings-function-mapping-tab">Function Mapping</TabsTrigger>
          <TabsTrigger value="escalation" data-testid="settings-escalation-tab">Escalation Matrix</TabsTrigger>
          <TabsTrigger value="distribution" data-testid="settings-distribution-tab">Distribution</TabsTrigger>
          <TabsTrigger value="google-ratings" data-testid="settings-google-ratings-tab-trigger">Google Ratings</TabsTrigger>
          <TabsTrigger value="oem-scorecard" data-testid="settings-oem-scorecard-tab">OEM Scorecard</TabsTrigger>
          <TabsTrigger value="data" data-testid="settings-data-tab">Data</TabsTrigger>
        </TabsList>
        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
        <TabsContent value="outlets" className="mt-4"><OutletsTab /></TabsContent>
        <TabsContent value="clusters" className="mt-4"><ClustersTab /></TabsContent>
        <TabsContent value="categories" className="mt-4"><CategoriesTab /></TabsContent>
        <TabsContent value="function-mapping" className="mt-4"><FunctionMapTab /></TabsContent>
        <TabsContent value="escalation" className="mt-4"><EscalationTab /></TabsContent>
        <TabsContent value="distribution" className="mt-4"><DistributionTab /></TabsContent>
        <TabsContent value="google-ratings" className="mt-4"><GoogleRatingsTab /></TabsContent>
        <TabsContent value="oem-scorecard" className="mt-4"><OemScorecardTab /></TabsContent>
        <TabsContent value="data" className="mt-4"><DataTab /></TabsContent>
      </Tabs>
    </div>
  );
}

const UsersTab = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const empty = { name: "", email: "", password: "", role: "crm_executive", outlet_id: "", cluster_id: "", function_scope: "Both", whatsapp_number: "+91", daily_report_enabled: true, report_delivery_time: "20:00" };
  const [form, setForm] = useState(empty);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/outlets").then((r) => setOutlets(r.data)).catch((e) => console.error("Background fetch failed:", e));
    api.get("/clusters").then((r) => setClusters(r.data)).catch((e) => console.error("Background fetch failed:", e));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const needsOutlet = ["outlet_sales_manager", "outlet_service_manager", "crm_executive"].includes(form.role);
  const needsCluster = ["cluster_head_sales", "cluster_head_service"].includes(form.role);

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) {
        const body = { name: form.name, role: form.role, outlet_id: form.outlet_id || null, cluster_id: form.cluster_id || null, function_scope: form.function_scope, daily_report_enabled: form.daily_report_enabled, report_delivery_time: form.report_delivery_time };
        if (form.whatsapp_number && form.whatsapp_number !== "+91") body.whatsapp_number = form.whatsapp_number;
        if (form.password) body.password = form.password;
        await api.patch(`/users/${editing.id}`, body);
        toast.success("User updated");
      } else {
        await api.post("/users", { ...form, outlet_id: form.outlet_id || null, cluster_id: form.cluster_id || null });
        toast.success("User created");
      }
      setOpen(false);
      setEditing(null);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "User deactivated" : "User activated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doDeleteUser = async () => {
    setBusy(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success(`User ${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const outletName = (id) => outlets.find((o) => o.id === id)?.name || "—";
  const clusterName = (id) => clusters.find((c) => c.id === id)?.name || "—";
  const userScopeLabel = (u) => {
    if (u.cluster_id) return clusterName(u.cluster_id);
    if (u.outlet_id) return outletName(u.outlet_id);
    return "All";
  };

  const exportNumbers = async () => {
    setExporting(true);
    try {
      const r = await api.get("/users/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "user_whatsapp_list.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("User list exported");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-4 px-0">
        <div className="flex justify-end gap-2 px-4 mb-3">
          <Button variant="outline" onClick={exportNumbers} disabled={exporting} data-testid="settings-export-users-button">
            {exporting ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Download size={15} className="mr-1.5" />}
            Export Numbers
          </Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]" data-testid="settings-add-user-button">
                <Plus size={15} className="mr-1.5" /> Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="user-dialog">
              <DialogHeader>
                <DialogTitle className="font-serif">{editing ? "Edit user" : "Create user"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Full name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
                </div>
                {!editing && (
                  <div className="space-y-1">
                    <Label className="text-xs">Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs">{editing ? "New password (leave blank to keep)" : "Password"}</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">WhatsApp number (with country code)</Label>
                  <Input value={form.whatsapp_number} placeholder="+919876543210"
                    onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })}
                    data-testid="user-whatsapp-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Daily report delivery</Label>
                    <div className="flex items-center gap-2 h-9">
                      <Switch checked={form.daily_report_enabled}
                        onCheckedChange={(v) => setForm({ ...form, daily_report_enabled: v })}
                        data-testid="user-daily-report-switch" />
                      <span className="text-sm">{form.daily_report_enabled ? "ON" : "OFF"}</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Delivery time (IST)</Label>
                    <Input type="time" value={form.report_delivery_time}
                      onChange={(e) => setForm({ ...form, report_delivery_time: e.target.value })}
                      data-testid="user-delivery-time-input" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, function_scope: SUGGESTED_FUNCTION[v] || "Both" })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {needsOutlet && (
                  <div className="space-y-1">
                    <Label className="text-xs">Outlet</Label>
                    <Select value={form.outlet_id} onValueChange={(v) => setForm({ ...form, outlet_id: v })}>
                      <SelectTrigger data-testid="user-outlet-select"><SelectValue placeholder="Select outlet" /></SelectTrigger>
                      <SelectContent className="max-h-60">
                        {outlets.map((o) => (
                          <SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {needsCluster && (
                  <div className="space-y-1">
                    <Label className="text-xs">Cluster</Label>
                    <Select value={form.cluster_id} onValueChange={(v) => setForm({ ...form, cluster_id: v })}>
                      <SelectTrigger data-testid="user-cluster-select"><SelectValue placeholder="Select cluster" /></SelectTrigger>
                      <SelectContent className="max-h-60">
                        {clusters.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name} ({c.outlets?.length || 0} outlets)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs">Function (admin-assigned: what this user sees & gets alerted on)</Label>
                  <Select value={form.function_scope} onValueChange={(v) => setForm({ ...form, function_scope: v })}>
                    <SelectTrigger data-testid="user-function-scope-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sales">Sales</SelectItem>
                      <SelectItem value="Service">Service</SelectItem>
                      <SelectItem value="Both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={busy} className="bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]" data-testid="user-save-button">
                  {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <Table className="cx-table" data-testid="users-table">
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Function</TableHead>
              <TableHead>WhatsApp</TableHead>
              <TableHead>Last seen</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="pr-4"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="pl-4 font-medium">
                  {u.name}
                  {u.failed_login_flag && (
                    <Badge className="ml-1.5 bg-[hsl(var(--cx-crimson))] text-white border-transparent text-[0.6rem]"
                      title="5+ failed login attempts today" data-testid={`user-failed-flag-${u.email}`}>
                      5+ fails
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-xs">{u.email}</TableCell>
                <TableCell className="text-xs">{ROLE_OPTS.find((r) => r.value === u.role)?.label || u.role}</TableCell>
                <TableCell className="text-xs">{userScopeLabel(u)}</TableCell>
                <TableCell className="text-xs" data-testid={`user-function-cell-${u.email}`}>{u.function_scope || "Both"}</TableCell>
                <TableCell className="text-xs tabular whitespace-nowrap" data-testid={`user-whatsapp-cell-${u.email}`}>
                  {u.whatsapp_number || <Badge variant="outline" className="text-[0.6rem] text-[hsl(var(--status-amber))] border-[hsl(var(--status-amber)/0.4)]">Pending</Badge>}
                </TableCell>
                <TableCell className="text-xs tabular whitespace-nowrap" data-testid={`user-last-seen-${u.email}`}>
                  {u.last_login_fmt || <span className="text-muted-foreground">Never</span>}
                </TableCell>
                <TableCell>
                  {u.role === "admin" ? (
                    <Badge variant="outline" className="text-[0.62rem] text-[hsl(var(--cx-gold))] border-[hsl(var(--cx-gold)/0.5)]"
                      title="Admin accounts cannot be deactivated" data-testid={`user-protected-badge-${u.email}`}>
                      Protected
                    </Badge>
                  ) : (
                    <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`user-active-switch-${u.email}`} />
                  )}
                </TableCell>
                <TableCell className="pr-4 whitespace-nowrap">
                  <Button variant="ghost" size="sm" className="h-8" data-testid={`user-edit-button-${u.email}`}
                    onClick={() => {
                      setEditing(u);
                      setForm({ name: u.name, email: u.email, password: "", role: u.role, outlet_id: u.outlet_id || "", cluster_id: u.cluster_id || "", function_scope: u.function_scope || "Both", whatsapp_number: u.whatsapp_number || "+91", daily_report_enabled: u.daily_report_enabled !== false, report_delivery_time: u.report_delivery_time || "20:00" });
                      setOpen(true);
                    }}>
                    <Pencil size={14} />
                  </Button>
                  {u.id !== me?.id && (
                    <Button variant="ghost" size="sm" className="h-8" data-testid={`user-delete-button-${u.email}`}
                      onClick={() => setDeleteTarget(u)} aria-label={`Delete ${u.name}`}>
                      <Trash2 size={14} className="text-muted-foreground hover:text-[hsl(var(--cx-crimson))]" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
          <DialogContent className="sm:max-w-md" data-testid="user-delete-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-[hsl(var(--cx-crimson))]">Delete user {deleteTarget?.name}?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Permanently removes <strong>{deleteTarget?.email}</strong> and their alert notifications.
              They will no longer be able to sign in. This cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="user-delete-cancel-button">Cancel</Button>
              <Button onClick={doDeleteUser} disabled={busy}
                className="bg-[hsl(var(--cx-crimson))] hover:bg-[hsl(var(--cx-crimson))]/90 text-white"
                data-testid="user-delete-confirm-button">
                {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Delete user
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const OUTLET_TYPES = ["Both", "Sales", "Service"];

const OutletTypeBadge = ({ type }) => {
  const t = type || "Both";
  const styles = {
    Sales: "text-[hsl(var(--cx-gold))] border-[hsl(var(--cx-gold)/0.45)] bg-[hsl(var(--accent))]",
    Service: "text-[hsl(var(--cx-navy))] border-[hsl(var(--cx-navy)/0.3)] bg-[hsl(var(--cx-navy)/0.06)]",
    Both: "text-muted-foreground",
  };
  return (
    <Badge variant="outline" className={styles[t]} data-testid="outlet-type-badge">
      {t === "Both" ? "Sales + Service" : `${t} only`}
    </Badge>
  );
};

const OutletsTab = () => {
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", active: true, type: "Both" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/outlets", { params: { include_inactive: true } }).then((r) => setOutlets(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/outlets/${editing.id}`, form);
        toast.success("Outlet updated");
      } else {
        await api.post("/outlets", form);
        toast.success("Outlet created");
      }
      setOpen(false);
      setEditing(null);
      setForm({ name: "", code: "", active: true, type: "Both" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const doDeleteOutlet = async () => {
    setBusy(true);
    try {
      await api.delete(`/outlets/${deleteTarget.id}`);
      toast.success(`Outlet ${deleteTarget.code} deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-4 px-0">
        <div className="flex justify-end px-4 mb-3">
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm({ name: "", code: "", active: true, type: "Both" }); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]" data-testid="settings-add-outlet-button">
                <Plus size={15} className="mr-1.5" /> Add Outlet
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-sm" data-testid="outlet-dialog">
              <DialogHeader>
                <DialogTitle className="font-serif">{editing ? "Edit outlet" : "Add outlet"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="outlet-name-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Code (e.g. KL308)</Label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="outlet-code-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Location type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="outlet-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OUTLET_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>{t === "Both" ? "Sales + Service" : `${t} only`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[0.68rem] text-muted-foreground">
                    Sales-only locations never appear in Service dashboards or leaderboards (and vice versa).
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="outlet-active-switch" />
                  <span className="text-sm">{form.active ? "Active" : "Inactive"}</span>
                </div>
                {!form.active && (
                  <p className="text-[0.68rem] text-[hsl(var(--status-amber))]">
                    Inactive locations keep their history for trends but are excluded from current dashboards,
                    escalations and leaderboards.
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={busy} className="bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]" data-testid="outlet-save-button">
                  {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <Table className="cx-table" data-testid="outlets-table">
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Origin</TableHead>
              <TableHead className="pr-4"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {outlets.map((o) => (
              <TableRow key={o.id} className={!o.active ? "opacity-60" : ""}>
                <TableCell className="pl-4 font-medium tabular">{o.code}</TableCell>
                <TableCell>{o.name}</TableCell>
                <TableCell>
                  <OutletTypeBadge type={o.type} />
                  {o.type_reviewed === false && (
                    <Badge variant="outline"
                      className="ml-1.5 text-[hsl(var(--status-amber))] border-[hsl(var(--status-amber)/0.5)] bg-[hsl(var(--status-amber)/0.08)]"
                      data-testid={`outlet-type-unreviewed-${o.code}`}>
                      <FileWarning size={11} className="mr-1" /> Type not reviewed
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={o.active ? "text-[hsl(var(--status-green))]" : "text-muted-foreground"}>
                    {o.active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{o.auto_created ? "Auto-created on import" : "Configured"}</TableCell>
                <TableCell className="pr-4 whitespace-nowrap">
                  <Button variant="ghost" size="sm" className="h-8" data-testid={`outlet-edit-button-${o.code}`}
                    onClick={() => { setEditing(o); setForm({ name: o.name, code: o.code, active: o.active, type: o.type || "Both" }); setOpen(true); }}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8" data-testid={`outlet-delete-button-${o.code}`}
                    onClick={() => setDeleteTarget(o)} aria-label={`Delete ${o.name}`}>
                    <Trash2 size={14} className="text-muted-foreground hover:text-[hsl(var(--cx-crimson))]" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
          <DialogContent className="sm:max-w-md" data-testid="outlet-delete-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif text-[hsl(var(--cx-crimson))]">Delete outlet {deleteTarget?.code}?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Permanently removes <strong>{deleteTarget?.name}</strong> and detaches it from any cluster.
              Deletion is only possible if the outlet has no complaints and no assigned users — otherwise
              deactivate it instead. This cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="outlet-delete-cancel-button">Cancel</Button>
              <Button onClick={doDeleteOutlet} disabled={busy}
                className="bg-[hsl(var(--cx-crimson))] hover:bg-[hsl(var(--cx-crimson))]/90 text-white"
                data-testid="outlet-delete-confirm-button">
                {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Delete outlet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

const CategoriesTab = () => {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/categories", { name });
      setName("");
      toast.success("Category added");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (c) => {
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Category removed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex gap-2 mb-4 max-w-md">
          <Input placeholder="New category name" value={name} onChange={(e) => setName(e.target.value)} data-testid="category-name-input" />
          <Button onClick={add} disabled={busy} className="bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]" data-testid="category-add-button">
            <Plus size={15} />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="categories-list">
          {cats.map((c) => (
            <Badge key={c.id} variant="outline" className="gap-1.5 py-1.5 px-3 text-xs">
              {c.name}
              <button onClick={() => del(c)} aria-label={`Delete ${c.name}`} data-testid={`category-delete-${c.name}`}>
                <Trash2 size={12} className="text-muted-foreground hover:text-[hsl(var(--cx-crimson))]" />
              </button>
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const FunctionMapTab = () => {
  const [map, setMap] = useState({});
  const [departments, setDepartments] = useState([]);
  const [unmapped, setUnmapped] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/config/function-map").then((r) => {
      setMap(r.data.map);
      setDepartments(r.data.departments);
      setUnmapped(r.data.unmapped_count);
    }).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/config/function-map", { map });
      toast.success(`Mapping saved. ${r.data.remapped_complaints} complaints re-routed.`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-4">
        <p className="text-sm text-muted-foreground mb-4">
          Each department routes to a function on upload. Complaints in departments mapped to “Unmapped” stay in the
          Unmapped bucket until you route them.{" "}
          {unmapped > 0 && (
            <span className="text-[hsl(var(--cx-crimson))] font-medium" data-testid="unmapped-count">
              {unmapped} complaints currently unmapped.
            </span>
          )}
        </p>
        <div className="space-y-2 max-w-md" data-testid="function-map-list">
          {departments.map((d) => (
            <div key={d} className="flex items-center justify-between border rounded-md px-3 py-2">
              <span className="text-sm font-medium">{d}</span>
              <Select value={map[d] || "Unmapped"} onValueChange={(v) => setMap({ ...map, [d]: v })}>
                <SelectTrigger className="h-8 w-[140px] text-xs" data-testid={`function-map-select-${d}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Sales", "Service", "Unmapped"].map((f) => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
        <Button onClick={save} disabled={busy} className="mt-4 bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]" data-testid="function-map-save-button">
          {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Save mapping
        </Button>
      </CardContent>
    </Card>
  );
};

const ClustersTab = () => {
  const [clusters, setClusters] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", outlet_ids: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/clusters").then((r) => setClusters(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/outlets").then((r) => setOutlets(r.data)).catch((e) => console.error("Background fetch failed:", e));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const toggleOutlet = (id) => {
    setForm((f) => ({
      ...f,
      outlet_ids: f.outlet_ids.includes(id) ? f.outlet_ids.filter((x) => x !== id) : [...f.outlet_ids, id],
    }));
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Cluster name is required");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/clusters/${editing.id}`, form);
        toast.success("Cluster updated");
      } else {
        await api.post("/clusters", form);
        toast.success("Cluster created");
      }
      setOpen(false);
      setEditing(null);
      setForm({ name: "", outlet_ids: [] });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (c) => {
    try {
      await api.delete(`/clusters/${c.id}`);
      toast.success("Cluster deleted");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Card>
      <CardContent className="pt-4 px-0">
        <div className="flex items-center justify-between px-4 mb-3 gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">
            Clusters group outlets under Cluster Heads (Sales / Service) — the L3 escalation tier.
          </p>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm({ name: "", outlet_ids: [] }); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]" data-testid="settings-add-cluster-button">
                <Plus size={15} className="mr-1.5" /> Add Cluster
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="cluster-dialog">
              <DialogHeader>
                <DialogTitle className="font-serif">{editing ? "Edit cluster" : "Create cluster"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs">Cluster name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cluster-name-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Outlets in this cluster ({form.outlet_ids.length} selected)</Label>
                  <div className="border rounded-md max-h-56 overflow-y-auto divide-y" data-testid="cluster-outlet-list">
                    {outlets.map((o) => (
                      <label key={o.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-[hsl(var(--cx-surface))]">
                        <Checkbox
                          checked={form.outlet_ids.includes(o.id)}
                          onCheckedChange={() => toggleOutlet(o.id)}
                          data-testid={`cluster-outlet-checkbox-${o.code}`}
                        />
                        <span className="text-sm">{o.code} · {o.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={busy} className="bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]" data-testid="cluster-save-button">
                  {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <Table className="cx-table" data-testid="clusters-table">
          <TableHeader>
            <TableRow>
              <TableHead className="pl-4">Cluster</TableHead>
              <TableHead>Outlets</TableHead>
              <TableHead>Cluster Heads</TableHead>
              <TableHead className="pr-4"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clusters.map((c) => (
              <TableRow key={c.id} data-testid={`cluster-row-${c.name}`}>
                <TableCell className="pl-4 font-medium">{c.name}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(c.outlets || []).map((o) => (
                      <Badge key={o.id} variant="outline" className="text-[0.65rem]">{o.code}</Badge>
                    ))}
                    {(c.outlets || []).length === 0 && <span className="text-xs text-muted-foreground">No outlets</span>}
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  {(c.heads || []).length ? c.heads.map((h) => h.name).join(", ") : <span className="text-muted-foreground">Unassigned</span>}
                </TableCell>
                <TableCell className="pr-4 whitespace-nowrap">
                  <Button variant="ghost" size="sm" className="h-8" data-testid={`cluster-edit-button-${c.name}`}
                    onClick={() => { setEditing(c); setForm({ name: c.name, outlet_ids: (c.outlets || []).map((o) => o.id) }); setOpen(true); }}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8" data-testid={`cluster-delete-button-${c.name}`}
                    onClick={() => del(c)}>
                    <Trash2 size={14} className="text-muted-foreground hover:text-[hsl(var(--cx-crimson))]" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const EscalationTab = () => {
  const [thresholds, setThresholds] = useState(null);
  const [labels, setLabels] = useState({});
  const [recipients, setRecipients] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/config/escalation").then((r) => {
      setThresholds(r.data.thresholds);
      setLabels(r.data.level_labels || {});
      setRecipients(r.data.recipients || {});
    }).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const t = { l2: Number(thresholds.l2), l3: Number(thresholds.l3), l4: Number(thresholds.l4), l5: Number(thresholds.l5) };
      const r = await api.put("/config/escalation", { thresholds: t });
      toast.success(`Thresholds saved — ${r.data.recomputed?.transitions ?? 0} complaints re-levelled.`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const ROLE_LABEL = {
    crm_executive: "CCM / CRM Executive", outlet_sales_manager: "Outlet Sales Manager",
    outlet_service_manager: "Outlet Service Manager", cluster_head_sales: "Cluster Head – Sales",
    cluster_head_service: "Cluster Head – Service", vp_sales: "VP Sales", vp_service: "VP Service", md: "Managing Director",
  };

  if (!thresholds) return <Card><CardContent className="pt-4"><p className="text-sm text-muted-foreground">Loading…</p></CardContent></Card>;

  return (
    <Card>
      <CardContent className="pt-4 space-y-5">
        <div>
          <p className="text-sm text-muted-foreground mb-3">
            Escalation levels are computed automatically from days open (working from the Salesforce reported date).
            L1 applies from day 0; set the day each higher level activates. Changing thresholds re-levels all open complaints immediately.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl" data-testid="escalation-thresholds-form">
            {["l2", "l3", "l4", "l5"].map((k) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs uppercase">{k.toUpperCase()} from day</Label>
                <Input
                  type="number" min={1} value={thresholds[k]}
                  onChange={(e) => setThresholds({ ...thresholds, [k]: e.target.value })}
                  data-testid={`escalation-threshold-${k}-input`}
                />
              </div>
            ))}
          </div>
          <Button onClick={save} disabled={busy} className="mt-4 bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]" data-testid="escalation-save-button">
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Save thresholds
          </Button>
        </div>

        <div>
          <h3 className="font-serif text-base mb-2">Alert routing (fixed matrix)</h3>
          <Table className="cx-table max-w-2xl" data-testid="escalation-routing-table">
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4">Level</TableHead>
                <TableHead>Escalates to</TableHead>
                <TableHead className="pr-4">Alerted roles</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {["1", "2", "3", "4", "5"].map((lv) => (
                <TableRow key={lv}>
                  <TableCell className="pl-4 font-semibold tabular">L{lv}</TableCell>
                  <TableCell>{labels[lv] || ""}</TableCell>
                  <TableCell className="pr-4 text-xs text-muted-foreground">
                    {(recipients[lv] || []).map((r) => ROLE_LABEL[r] || r).join(", ")}
                    {lv === "5" && " — consolidated daily digest (Critical priority alerts immediately)"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

const DataTab = () => {
  const [total, setTotal] = useState(null);
  const [imports, setImports] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/complaints", { params: { page_size: 1 } }).then((r) => setTotal(r.data.total)).catch((e) => console.error("Background fetch failed:", e));
    api.get("/imports").then((r) => setImports(r.data.length)).catch((e) => console.error("Background fetch failed:", e));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const clearAll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/clear-complaint-data", { confirm: confirmText });
      toast.success(`Cleared ${r.data.deleted.complaints.toLocaleString()} complaints and ${r.data.deleted.imports} import records.`);
      setOpen(false);
      setConfirmText("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-4 space-y-5">
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div className="border rounded-md p-3 text-center">
            <p className="kpi-value text-2xl" data-testid="data-tab-complaint-count">{total === null ? "…" : total.toLocaleString()}</p>
            <p className="text-[0.6rem] uppercase tracking-wider text-muted-foreground mt-0.5">Complaints in system</p>
          </div>
          <div className="border rounded-md p-3 text-center">
            <p className="kpi-value text-2xl" data-testid="data-tab-import-count">{imports === null ? "…" : imports}</p>
            <p className="text-[0.6rem] uppercase tracking-wider text-muted-foreground mt-0.5">Import batches</p>
          </div>
        </div>

        <div className="border border-[hsl(var(--cx-crimson)/0.35)] rounded-lg p-4 bg-[hsl(var(--status-red)/0.03)] max-w-2xl" data-testid="data-danger-zone">
          <h3 className="font-serif text-base text-[hsl(var(--cx-crimson))] mb-1">Danger zone — clear all complaint data</h3>
          <p className="text-sm text-muted-foreground mb-1">
            Permanently deletes <strong>all complaints, import history and escalation alerts</strong>. Use this to reset before
            uploading a fresh Salesforce dump.
          </p>
          <p className="text-sm text-muted-foreground mb-3">
            Kept: user accounts, outlets, clusters, categories, function mapping and escalation thresholds.
          </p>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConfirmText(""); }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[hsl(var(--cx-crimson)/0.5)] text-[hsl(var(--cx-crimson))] hover:bg-[hsl(var(--cx-crimson)/0.06)] hover:text-[hsl(var(--cx-crimson))]"
                data-testid="clear-data-open-button">
                <Trash2 size={15} className="mr-1.5" /> Clear All Complaint Data
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="clear-data-dialog">
              <DialogHeader>
                <DialogTitle className="font-serif text-[hsl(var(--cx-crimson))]">Clear all complaint data?</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                This deletes {total === null ? "all" : total.toLocaleString()} complaints, all import history and all
                escalation alerts. <strong>This cannot be undone.</strong> Users, outlets, clusters and settings are kept.
              </p>
              <div className="space-y-1">
                <Label className="text-xs">Type <span className="font-mono font-bold">DELETE</span> to confirm</Label>
                <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE"
                  data-testid="clear-data-confirm-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} data-testid="clear-data-cancel-button">Cancel</Button>
                <Button onClick={clearAll} disabled={busy || confirmText !== "DELETE"}
                  className="bg-[hsl(var(--cx-crimson))] hover:bg-[hsl(var(--cx-crimson))]/90 text-white"
                  data-testid="clear-data-confirm-button">
                  {busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Permanently delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <p className="text-xs text-muted-foreground max-w-2xl">
          After clearing, go to <strong>Upload Data</strong> and upload the latest Salesforce dump — dashboards, RAG aging
          and the L1–L5 escalation matrix rebuild automatically.
        </p>
      </CardContent>
    </Card>
  );
};

/* ================= Phase 9: Report Distribution Matrix ================= */
const CHANNELS = [
  { key: "in_app", label: "In-App", status: "active" },
  { key: "email", label: "Email", status: "coming_soon" },
  { key: "whatsapp", label: "WhatsApp", status: "coming_soon" },
];
const REPORT_TYPE_LABELS = { daily: "Daily Report", weekly: "Weekly Review", monthly: "Monthly Report" };

const DistributionTab = () => {
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/config/distribution").then((r) => {
      setData(r.data);
      setMatrix(r.data.matrix);
    }).catch((e) => toast.error(errMsg(e)));
    api.get("/distribution/log").then((r) => setLog(r.data)).catch((e) => console.error("Background fetch failed:", e));
  }, []);

  const toggle = (rt, role, ch) => {
    setMatrix((m) => ({
      ...m,
      [rt]: { ...m[rt], [role]: { ...m[rt][role], [ch]: !m[rt][role][ch] } },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/config/distribution", { matrix });
      toast.success("Distribution matrix saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (!data || !matrix) return <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-5 space-y-4">
          <div>
            <h3 className="font-serif text-lg">Report Distribution Matrix</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Control which roles receive each report. In-app delivery is live; email and WhatsApp are structurally
              ready and switch on automatically once the channels are configured.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {CHANNELS.map((c) => (
              <Badge key={c.key} variant="outline"
                className={c.status === "active"
                  ? "text-[hsl(var(--status-green))] border-[hsl(var(--status-green)/0.35)]"
                  : "text-muted-foreground"}>
                {c.label}: {c.status === "active" ? "Active" : "Coming soon"}
              </Badge>
            ))}
          </div>
          {Object.keys(matrix).map((rt) => (
            <div key={rt} className="border rounded-lg overflow-hidden">
              <p className="text-xs uppercase tracking-[0.12em] font-medium px-4 py-2 bg-[hsl(var(--cx-surface-2))]">{REPORT_TYPE_LABELS[rt] || rt}</p>
              <div className="overflow-x-auto">
                <Table className="cx-table" data-testid={`distribution-table-${rt}`}>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-4">Role</TableHead>
                      {CHANNELS.map((c) => <TableHead key={c.key} className="text-center">{c.label}</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data.roles || []).map((role) => (
                      <TableRow key={role.value}>
                        <TableCell className="pl-4 text-sm">{role.label}</TableCell>
                        {CHANNELS.map((c) => (
                          <TableCell key={c.key} className="text-center">
                            <Checkbox
                              checked={Boolean(matrix[rt]?.[role.value]?.[c.key])}
                              disabled={c.status !== "active"}
                              onCheckedChange={() => toggle(rt, role.value, c.key)}
                              data-testid={`dist-${rt}-${role.value}-${c.key}`}
                              aria-label={`${REPORT_TYPE_LABELS[rt]} to ${role.label} via ${c.label}`}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
          <Button onClick={save} disabled={saving} className="bg-[hsl(var(--cx-navy))] hover:bg-[hsl(var(--cx-navy-2))]" data-testid="distribution-save-button">
            {saving && <Loader2 size={14} className="animate-spin mr-1.5" />} Save Matrix
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-5">
          <h3 className="font-serif text-lg mb-2">Delivery Log</h3>
          {log.length === 0 ? (
            <p className="text-sm text-muted-foreground pb-2">No deliveries recorded yet — entries appear when reports are generated.</p>
          ) : (
            <Table className="cx-table" data-testid="delivery-log-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-4">When</TableHead>
                  <TableHead>Report</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead className="text-right">Recipients</TableHead>
                  <TableHead className="pr-4">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {log.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="pl-4 tabular text-xs whitespace-nowrap">{dayjs(l.at).format("DD MMM YY, HH:mm")}</TableCell>
                    <TableCell className="capitalize">{l.report_type}</TableCell>
                    <TableCell className="uppercase text-xs">{l.channel.replace("_", "-")}</TableCell>
                    <TableCell className="capitalize text-xs">{l.trigger}</TableCell>
                    <TableCell className="text-right tabular">{l.recipients}</TableCell>
                    <TableCell className="pr-4">
                      <Badge variant="outline" className="text-[hsl(var(--status-green))] border-[hsl(var(--status-green)/0.35)] text-[0.65rem]">
                        {l.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
